import { Col, ConfigProvider, Input, Row, Slider, Spin } from "antd";
import { motion, useReducedMotion } from "framer-motion";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toGetService } from "../../../apis/actions/service.actions";
import { toGetBrands, toGetModel } from "../../../apis/actions/vehicle.actions";
import { toAddOrRemoveService } from "../../../apis/slices/globalSlice";
import { AppDispatch, RootState } from "../../../apis/store";
import ListFetchLoader from "../../../Components/biblio/ListFetchLoader";
import PaginationComponent from "../../../Components/biblio/PaginationComponent";
import PageHeaderAllPage from "../../../Components/layouts/PageHeaderAllPage";
import ServiceCategoryFilter from "../../../Components/shop/ServiceCategoryFilter";
import { scroll } from "../../../utils/Utils";
import { signalCartAddedFeedback } from "../../../utils/cartAddedFeedback";
import Navbar from "../../Menu/Navbar";
import Footer from "../Sections/Footer";
import Item from "./Item";
import {
  shopGridContainerReduced,
  shopGridContainerVariants,
  shopGridItemReduced,
  shopGridItemVariants,
} from "./shopGridMotion";
import { useTranslation } from "react-i18next";
import { SearchOutlined } from "@ant-design/icons";
import { MdFilterAltOff, MdFilterAlt } from "react-icons/md";
import {
  markServicesFetched,
  shouldSkipServicesFetch,
} from "../../../utils/shopListCache";
import { resolveServicesVehicleId } from "../../../utils/resolveServicesVehicleId";
import { consumeListScroll, buildDetailNavState } from "../../../utils/shopListReturn";
import { useShopListQuery } from "../../../utils/shopListQuery";
import {
  resolveCategoryId,
  resolveListSort,
  SHOP_LIST_PAGE_SIZES,
} from "../../../utils/paginatedList";
import {
  getServiceCatalogDiscount,
  getServiceCatalogPrice,
  getServiceFilterPrice,
  getServiceWorkHours,
  hasServiceVariants,
  resolveServiceCartPayload,
} from "../../../utils/serviceCatalog";

const ServiceShopItem = memo(function ServiceShopItem({ el }: { el: any }) {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const toAddService = useCallback(
    (_inCart: boolean) => {
      if (hasServiceVariants(el)) {
        navigate("/detail", {
          state: buildDetailNavState({
            id: el.id,
            type: "service",
            returnTo: "/services",
            name: el.name,
            price: getServiceCatalogPrice(el),
          }),
        });
        return;
      }
      const payload = resolveServiceCartPayload(el);
      if (!payload) return;
      signalCartAddedFeedback();
      dispatch(toAddOrRemoveService(payload));
    },
    [dispatch, el, navigate]
  );
  return (
    <Item
      feature_ids={el.feature_ids}
      description_website={el.description_website}
      type="service"
      id={el.id}
      name={el.name}
      description={el.description}
      category={el.categ_id}
      discount={getServiceCatalogDiscount(el)}
      duration={getServiceWorkHours(el)}
      warranty={el.warranty}
      img={el.image_1920}
      price={getServiceCatalogPrice(el)}
      toAddService={toAddService}
    />
  );
});

const ServicesList = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();
  const { services } = useSelector((state: RootState) => state?.services);
  const { vehicle, browseServicesWithoutVehicle } = useSelector((state: RootState) => state?.global);
  const {
    page: currentPage,
    limit: itemPerPage,
    search,
    sort: sortOption,
    categoryId,
    setPage,
    setLimit,
    setSearch,
    setSort,
    setCategoryId,
  } = useShopListQuery();
  const [apply, setApply] = useState<boolean>(false);
  const [displaybleServices, setDisplayableServices] = useState<any>([]);
  const currentRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    dispatch(toGetBrands(i18n.language));
    dispatch(toGetModel());
  }, [dispatch]);

  useEffect(() => {
    const vehicleId = resolveServicesVehicleId(browseServicesWithoutVehicle ? null : vehicle?.id);
    const lang = i18n.language;
    if (
      shouldSkipServicesFetch(
        vehicleId,
        lang,
        currentPage,
        itemPerPage,
        categoryId,
        services?.status ?? "idle",
        sortOption
      )
    ) {
      return;
    }
    markServicesFetched(vehicleId, lang, currentPage, itemPerPage, categoryId, sortOption);
    dispatch(
      toGetService({
        vehicleId,
        lang,
        page: currentPage,
        limit: itemPerPage,
        categoryId,
        sort: sortOption,
      })
    );
    // Avoid rejected→retry loading loops
  }, [
    dispatch,
    i18n.language,
    vehicle?.id,
    browseServicesWithoutVehicle,
    currentPage,
    itemPerPage,
    categoryId,
    sortOption,
  ]);

  useEffect(() => {
    const y = consumeListScroll("/services");
    if (y == null) return;
    const id = window.requestAnimationFrame(() => {
      window.scrollTo({ top: y, behavior: "auto" });
    });
    return () => window.cancelAnimationFrame(id);
  }, []);

  const isServicesLoading =
    services?.status === "loading" ||
    (services?.request != null &&
      (services.request.page !== currentPage ||
        services.request.limit !== itemPerPage ||
        services.request.categoryId !== resolveCategoryId(categoryId) ||
        services.request.sort !== resolveListSort(sortOption))) ||
    (services?.status === "idle" &&
      (!Array.isArray(services?.list) || services.list.length === 0));
  const catalogTotal = Number(services?.total) || 0;

  const maxPrice =
    services?.list?.length > 0
      ? Math.max(...services.list.map((item: any) => getServiceFilterPrice(item)))
      : 0;

  const [range, setRange] = useState([0, maxPrice]);

  useEffect(() => {
    setRange([0, maxPrice]);
  }, [maxPrice]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    toFilter(services?.list, apply, search);
  }, [services?.list, search, sortOption]);

  useEffect(() => {
    if (isServicesLoading || catalogTotal <= 0) return;
    const maxPage = Math.max(1, Math.ceil(catalogTotal / itemPerPage));
    if (currentPage > maxPage) setPage(maxPage);
  }, [isServicesLoading, catalogTotal, currentPage, itemPerPage, setPage]);

  const handleRangeChange = (value: any) => {
    setRange(value);
  };

  const toFilter = (list: any, apply: boolean, search: string) => {
    if (!list) return;

    let l: any = [...list];

    const [minPrice, maxPrice] = range;

    if (search !== "") {
      l = l.filter((el: any) =>
        el.name.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (apply && maxPrice !== -1 && maxPrice !== 0) {
      l = l.filter(
        (item: any) =>
          getServiceFilterPrice(item) >= minPrice &&
          getServiceFilterPrice(item) <= maxPrice
      );
    }

    // ponytail: sorting is server-side via `sort`; no client re-sort

    setDisplayableServices(l);
  };
  const sortLabels: Record<string, string> = {
    priceLowToHigh:
      i18n.language === "ar"
        ? "السعر : من الأقل إلى الأعلى"
        : "Price: Low to High",
    priceHighToLow:
      i18n.language === "ar"
        ? "السعر : من الأعلى إلى الأقل"
        : "Price: High to Low",
    nameAZ:
      i18n.language === "ar" ? "الاسم : من الألف إلى الياء" : "Name: A to Z",
    topRated: i18n.language === "ar" ? "الأكثر مبيعًا" : "Best Sellers",
  };

  return (
    <>
      <Navbar type="internal" />
      <main className="main">
        <PageHeaderAllPage
          tone="brand"
          showMenu
          current={t("menu.services")}
          banner="assets/imgs/page-header/car1.png"
          link="services"
        />
        <section
          className="box-section block-content-tourlist background-body ac-page-shop-services"
          ref={currentRef}
        >
          <div className="container ac--font-expo">
            <ServiceCategoryFilter
              selectedId={categoryId}
              onSelect={setCategoryId}
            />
            <div className="box-content-main pt-20">
              <div className="content-right">
                <div className="box-filters mb-25 pb-5 border-bottom border-1">
                  <div className="row align-items-center">
                    <div className="col-xl-3 col-md-3 mb-10 text-lg-start text-center">
                      <div className="box-view-type">
                        <span className="text-sm-bold neutral-500 number-found d-inline-flex align-items-center gap-2 flex-wrap justify-content-center justify-content-lg-start">
                          {isServicesLoading ? (
                            <>
                              <Spin size="small" />
                              <span>{t("services.loadingData")}</span>
                            </>
                          ) : (
                            <>
                              {t("services.items", { count: catalogTotal })}
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="col-xl-5 col-md-5 mb-10">
                      <div className="ac-shop-search-wrap">
                        <Input
                          allowClear
                          placeholder={t("services.search")}
                          prefix={<SearchOutlined />}
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="ac-search-input"
                          size="large"
                        />
                      </div>
                    </div>
                    <div className="col-xl-4 col-md-4 mb-10 text-lg-end text-center">
                      <div className="box-item-sort">
                        <span
                          className={`text-xs-medium neutral-500 ${
                            i18n.language === "ar" ? "ml-5" : "mr-5"
                          }`}
                        >
                          {t("services.sortBy")}
                        </span>

                        <div className="item-sort border-1">
                          <div className="dropdown dropdown-sort border-1-right">
                            <button
                              className="btn dropdown-toggle"
                              id="dropdownSort"
                              type="button"
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            >
                              <span>{sortLabels[sortOption]}</span>
                            </button>
                            <ul
                              className="dropdown-menu dropdown-menu-light m-0"
                              aria-labelledby="dropdownSort"
                            >
                              {Object.keys(sortLabels).map((key) => (
                                <li className="hover-white" key={key}>
                                  <a
                                    className={`dropdown-item ${
                                      sortOption === key ? "active" : ""
                                    }`}
                                    href="#"
                                    onClick={() => setSort(key)}
                                  >
                                    {sortLabels[key]}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="box-grid-tours">
                  {isServicesLoading ? (
                    <ListFetchLoader loading minHeight={400} />
                  ) : (
                    <motion.div
                      className="w-100"
                      key={`${currentPage}-${sortOption}-${i18n.language}-${displaybleServices
                        .map((p: { id: number }) => p.id)
                        .join("-")}`}
                      variants={
                        prefersReducedMotion
                          ? shopGridContainerReduced
                          : shopGridContainerVariants
                      }
                      initial="hidden"
                      animate="visible"
                    >
                      <Row gutter={16} justify={isMobile ? "center" : undefined}>
                        {displaybleServices.map((el: any) => (
                            <Col
                              key={el.id}
                              xxl={6}
                              xl={6}
                              lg={8}
                              md={12}
                              sm={18}
                              xs={20}
                            >
                              <motion.div
                                className="h-100"
                                variants={
                                  prefersReducedMotion
                                    ? shopGridItemReduced
                                    : shopGridItemVariants
                                }
                              >
                                <ServiceShopItem el={el} />
                              </motion.div>
                            </Col>
                          ))}
                      </Row>
                    </motion.div>
                  )}
                </div>

                {!isServicesLoading && catalogTotal > 0 && (
                  <PaginationComponent
                    total={catalogTotal}
                    itemPerPage={itemPerPage}
                    pageSizeOptions={[...SHOP_LIST_PAGE_SIZES]}
                    active={currentPage}
                    setActive={(nb) => {
                      scroll(currentRef);
                      setPage(nb);
                    }}
                    onPageSizeChange={(size) => {
                      scroll(currentRef);
                      setLimit(size);
                    }}
                  />
                )}
              </div>

              {/*
              <div className="content-left order-lg-first">
                <div className="sidebar-left ac-shop-sidebar">
                  <div className="box-filters-sidebar">
                    <div className="block-filter ac-shop-filter-panel">
                      <h6 className="text-lg-bold item-collapse neutral-1000">
                        {t("services.filter.price")}
                      </h6>
                      <div className="box-collapse scrollFilter">
                        <div className="pt-20">
                          <div className="box-slider-range">
                            <div>
                              <Slider
                                range
                                min={0}
                                max={maxPrice || 1}
                                value={range}
                                onChange={handleRangeChange}
                                reverse={i18n.language == "ar"}
                                tipFormatter={(value) => (
                                  <div className="ac--slider-tooltip">
                                    <span className="icon-saudi_riyal">
                                      {value}
                                    </span>
                                  </div>
                                )}
                                className="ac--slider-container-range"
                              />
                            </div>
                            <div className="box-value-price ac-shop-price-range-labels">
                              <span className="text-md-medium neutral-1000 icon-saudi_riyal">
                                {range[0]}
                              </span>
                              <span className="text-md-medium neutral-1000 icon-saudi_riyal">
                                {range[1]}
                              </span>
                            </div>
                            <input
                              className="value-money"
                              type="hidden"
                              value={range.join(",")}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="ac-shop-filter-actions">
                        <button
                          type="button"
                          className="ac-shop-filter-actions__clear"
                          onClick={() => {
                            setApply(false);
                            toFilter(
                              services?.list,
                              false,
                              search
                            );
                          }}
                        >
                          <MdFilterAltOff
                            aria-hidden
                            color="currentColor"
                            size={18}
                          />
                          {t("services.filter.all")}
                        </button>
                        <button
                          type="button"
                          className="ac-shop-filter-actions__apply"
                          onClick={() => {
                            setApply(true);
                            toFilter(
                              services?.list,
                              true,
                              search
                            );
                          }}
                        >
                          <MdFilterAlt aria-hidden color="currentColor" size={18} />
                          {t("profile.order.cart.apply")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              */}
            </div>
          </div>
          <Footer type="external" />
        </section>
      </main>
    </>
  );
};
export default ServicesList;
